import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import usePaymentMethodService from '../../app/services/hooks/usePaymentMethodService';
import { PaymentMethod, PaymentMethodFilters } from '../../app/models/PaymentMethod';
import PaymentMethodDataTable from './components/tables/PaymentMethodDataTable';
import PaymentMethodCreate from './components/PaymentMethodCreate';
import PaymentMethodEdit from './components/PaymentMethodEdit';
import useNavigationPage from '../../hooks/useNavigationPage';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Formas de Pago',
        url: '/payment-methods',
        isActive: true
    }
];

const PaymentMethods = () => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { fetchingGetPaymentMethods, getPaymentMethods } = usePaymentMethodService();
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const { navigationPage } = useNavigationPage();

    const [paymentMethodIdEdit, setPaymentMethodIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);

    const [paymentMethodFilter, setPaymentMethodFilter] = useState<PaymentMethodFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc',
        name: '',
        is_active: ''
    });

    useEffect(() => {
        reloadTable();
    }, [paymentMethodFilter]);

    const getAllPaymentMethods = () => {
        getPaymentMethods(paymentMethodFilter, {
            onSuccess: (response: ServiceResponse) => {
                setPaymentMethods(response.data.payment_methods);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message, {
                    autoClose: 2000
                });
                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showEdit = (paymentMethodId: number) => {
        setShowingEdit(true);
        setPaymentMethodIdEdit(paymentMethodId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setPaymentMethodIdEdit(-1);
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const reloadTable = () => {
        getAllPaymentMethods();
    };

    return (
        <>
            <Breadcrumbs pageSection="Formas de Pago" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nueva Forma de Pago" />}
                />
                <PaymentMethodDataTable
                    paymentMethods={paymentMethods}
                    edit={showEdit}
                    loading={fetchingGetPaymentMethods}
                    filter={paymentMethodFilter}
                    setFilter={setPaymentMethodFilter}
                    totalRows={totalRows}
                    callbackSuccess={reloadTable}
                />
            </DefaultCard>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear Forma de Pago"
                    backdrop={true}
                    showFooter={false}
                >
                    <PaymentMethodCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar Forma de Pago"
                    backdrop={true}
                    showFooter={false}
                >
                    <PaymentMethodEdit
                        paymentMethodId={paymentMethodIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default PaymentMethods;
